export const getChatUser = (currentUserId: number, users: User[]): User | undefined => {
  return users.find((u) => u.id !== currentUserId);
};

export const getChatUserName = (currentUserId: number, users: User[]): string => {
  const chatUser = getChatUser(currentUserId, users);
  if (!chatUser) return 'Unknown';
  return `${chatUser.firstName} ${chatUser.lastName}`.trim();
};

export const getInitials = (firstName: string, lastName: string): string => {
  const first = firstName.charAt(0).toUpperCase();
  const last = lastName.charAt(0).toUpperCase();
  return `${first}${last}`;
};

export const formatMessageTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export const formatMessageTimestamp = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

export const formatDateSeparator = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';

  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
};

export const getLatestMessage = <T extends { createdAt: string }>(messages: T[]): T | null => {
  if (messages.length === 0) return null;
  return [...messages].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  )[0];
};

export const isSameDay = (date1: string, date2: string): boolean => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
};

export const groupMessagesByDate = <T extends { createdAt: string }>(
  messages: T[]
): { date: string; messages: T[] }[] => {
  const groups: { date: string; messages: T[] }[] = [];
  const sorted = [...messages].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );

  sorted.forEach((message) => {
    const lastGroup = groups[groups.length - 1];
    if (lastGroup && isSameDay(lastGroup.date, message.createdAt)) {
      lastGroup.messages.push(message);
    } else {
      groups.push({ date: message.createdAt, messages: [message] });
    }
  });

  return groups;
};
